// DTO ⇄ view model for Manage customers. build… = to server, process… = to screen.
import { FACILITY_KINDS, buildFacilityValue, parseFacilityValue } from '@/config/formOptions';
import { customerSiteCertStatusLabel } from '@/config/statuses';
import { formatDate, formatDateInput, formatDateOrDash } from '@/lib/format/date';
import { formatFactorOrDash, formatNumber, formatNumberOrDash } from '@/lib/format/number';
import { formatContact, formatTextOrDash } from '@/lib/format/text';
import { formValue } from '@/lib/mappers/formValues';
import { joinReferenceId, splitReferenceId } from '@/lib/utils/id';
import {
  CUSTOMER_FIELDS,
  EMPTY_CUSTOMER_FORM,
  FIRST_SITE_KEY,
  SITE_FIELDS,
  SITE_KEYS_FIELD,
  siteFieldName,
} from '../constants/customers.constants';
import {
  EMPTY_TRANSPORT_FORM,
  TRANSPORT_FIELDS,
  TRANSPORT_MODES,
  TRANSPORT_MODE_FIELDS,
  transportFieldName,
} from '../constants/transportation.constants';
import { modeEmissions, siteKeysOf, totalTransportEmissions } from '../utils/customersUtils';

// POST /customers/:id/shipping-details reads dates as DD-MM-YYYY.
const SITE_DTO_DATE_FORMAT = 'dd-MM-yyyy';

const trimmed = (value) => String(value ?? '').trim();
const orNull = (value) => trimmed(value) || null;

const customerFormValues = (dto) => ({
  ...EMPTY_CUSTOMER_FORM,
  [CUSTOMER_FIELDS.organizationName]: formValue(dto.company_name),
  ...splitReferenceId(dto.customer_id),
  [CUSTOMER_FIELDS.primaryEmail]: formValue(dto.email),
  [CUSTOMER_FIELDS.primaryContactName]: formValue(dto.user_name),
  [CUSTOMER_FIELDS.addressLine1]: formValue(dto.address_line_1),
  [CUSTOMER_FIELDS.addressLine2]: formValue(dto.address_line_2),
  [CUSTOMER_FIELDS.city]: formValue(dto.city),
  // TODO: country to be added later — no customer response carries one
  [CUSTOMER_FIELDS.country]: formValue(dto.country),
  [CUSTOMER_FIELDS.zipCode]: formValue(dto.zip_code),
});

// A list row — GET /customers carries no address and no timestamp.
export const processCustomer = (dto) => ({
  id: String(dto.customer_account_id),
  customerName: formatTextOrDash(dto.company_name),
  customerId: formatTextOrDash(dto.customer_id),
  shippingSiteCount: formatNumberOrDash(dto.shipping_sites_count),
  primaryContact: formatContact(dto.user_name, dto.email),
  // TODO: last updated to be added later — the list response has no updated_at
  lastUpdated: formatDateOrDash(dto.updated_at),
});

export const processCustomersPage = (dto) => ({
  rows: (dto?.output?.customers ?? []).map(processCustomer),
  totalCount: dto?.output?.pagination?.total ?? dto?.output?.total ?? 0,
});

export const processCustomerDetail = (dto) => {
  if (!dto?.customer_account_id) return null;
  return {
    id: String(dto.customer_account_id),
    customerName: trimmed(dto.company_name) || '-',
    customerId: formatTextOrDash(dto.customer_id),
    // The supplier-customer connection's own reference number, not an editable form field.
    contractNumber: formatTextOrDash(dto.contract_number),
    primaryContactName: formatTextOrDash(dto.user_name),
    primaryEmail: formatTextOrDash(dto.email),
    addressLine1: formatTextOrDash(dto.address_line_1),
    addressLine2: formatTextOrDash(dto.address_line_2),
    city: formatTextOrDash(dto.city),
    country: formatTextOrDash(dto.country),
    zipCode: formatTextOrDash(dto.zip_code),
    lastUpdated: formatDateOrDash(dto.updated_at),
    formValues: customerFormValues(dto),
  };
};

// POST/PUT /customers read camelCase, unlike every other customer route.
export const buildCustomerDto = (values) => ({
  companyName: trimmed(values[CUSTOMER_FIELDS.organizationName]),
  customerId: joinReferenceId({
    idPrefix: values[CUSTOMER_FIELDS.idPrefix],
    idNumber: trimmed(values[CUSTOMER_FIELDS.idNumber]),
  }),
  email: orNull(values[CUSTOMER_FIELDS.primaryEmail]),
  userName: orNull(values[CUSTOMER_FIELDS.primaryContactName]),
  addressLine1: trimmed(values[CUSTOMER_FIELDS.addressLine1]),
  addressLine2: orNull(values[CUSTOMER_FIELDS.addressLine2]),
  city: trimmed(values[CUSTOMER_FIELDS.city]),
  // TODO: wire real API — no customer route accepts a country yet
  country: trimmed(values[CUSTOMER_FIELDS.country]),
  zipCode: trimmed(values[CUSTOMER_FIELDS.zipCode]),
});

// The "Shipping to" input is stored as `shipping_from`; `shipping_to` is the fallback.
const siteName = (dto) => trimmed(dto.shipping_from) || trimmed(dto.shipping_to);

export const processCustomerSite = (dto) => {
  const statusCode = dto.verification_status ?? null;
  return {
    id: String(dto.id),
    shippingTo: siteName(dto) || '-',
    shippingSiteId: formatTextOrDash(dto.shipping_id),
    managerEmail: formatTextOrDash(dto.email ?? dto.unverified_user?.email),
    managerName: formatTextOrDash(dto.site_manager_name ?? dto.unverified_user?.name),
    isccCertificateNumber: formatTextOrDash(dto.certificate_number),
    validTill: formatDateOrDash(dto.valid_to),
    statusCode,
    status: customerSiteCertStatusLabel(statusCode) ?? '-',
    addressLine1: formatTextOrDash(dto.address_line_1),
    addressLine2: formatTextOrDash(dto.address_line_2),
    city: formatTextOrDash(dto.city),
    // TODO: country to be added later — shipping_details has no country column
    country: formatTextOrDash(dto.country),
    zipCode: formatTextOrDash(dto.zip_code),
  };
};

export const processCustomerSitesPage = (dto) => {
  const tab = dto?.output?.site_details;
  return {
    rows: (tab?.site_details ?? []).map(processCustomerSite),
    totalCount: tab?.total ?? 0,
  };
};

// One saved site as a single-card sites form; the saved id sits whole in the number box.
export const processCustomerSiteForm = (dto) => {
  if (!dto) return null;
  const key = FIRST_SITE_KEY;
  const field = (name) => siteFieldName(key, name);
  const { idPrefix, idNumber } = splitReferenceId(dto.shipping_id);
  return {
    [SITE_KEYS_FIELD]: [key],
    [field(SITE_FIELDS.id)]: String(dto.id),
    [field(SITE_FIELDS.shippingTo)]: formValue(siteName(dto)),
    [field(SITE_FIELDS.idPrefix)]: idPrefix,
    [field(SITE_FIELDS.idNumber)]: idNumber,
    [field(SITE_FIELDS.managerName)]: formValue(dto.site_manager_name),
    [field(SITE_FIELDS.managerEmail)]: formValue(dto.email),
    [field(SITE_FIELDS.isccCertificateNumber)]: formValue(dto.certificate_number),
    [field(SITE_FIELDS.isccVerified)]: Boolean(dto.is_certificate_verified),
    [field(SITE_FIELDS.validFrom)]: formatDateInput(dto.valid_from),
    [field(SITE_FIELDS.validTill)]: formatDateInput(dto.valid_to),
    [field(SITE_FIELDS.addressLine1)]: formValue(dto.address_line_1),
    [field(SITE_FIELDS.addressLine2)]: formValue(dto.address_line_2),
    [field(SITE_FIELDS.city)]: formValue(dto.city),
    [field(SITE_FIELDS.country)]: formValue(dto.country),
    [field(SITE_FIELDS.zipCode)]: formValue(dto.zip_code),
  };
};

export const findCustomerSiteForm = (dto, siteId) =>
  processCustomerSiteForm(
    (dto?.output?.shipping_details ?? []).find((site) => String(site.id) === String(siteId)),
  );

const siteValue = (values, siteKey, field) => values?.[siteFieldName(siteKey, field)];

// One card → one `shipping_details` item (snake_case, as the bulk route reads it).
export const buildCustomerSiteDto = (values, siteKey) => {
  const certificateNumber = trimmed(siteValue(values, siteKey, SITE_FIELDS.isccCertificateNumber));
  const isVerified =
    certificateNumber !== '' && siteValue(values, siteKey, SITE_FIELDS.isccVerified) === true;
  return {
    shipping_from: trimmed(siteValue(values, siteKey, SITE_FIELDS.shippingTo)),
    shipping_id: joinReferenceId({
      idPrefix: siteValue(values, siteKey, SITE_FIELDS.idPrefix),
      idNumber: trimmed(siteValue(values, siteKey, SITE_FIELDS.idNumber)),
    }),
    site_manager_name: orNull(siteValue(values, siteKey, SITE_FIELDS.managerName)),
    email: orNull(siteValue(values, siteKey, SITE_FIELDS.managerEmail)),
    certificate_number: certificateNumber || null,
    is_certificate_verified: isVerified,
    ...(isVerified
      ? {
          valid_from:
            formatDate(siteValue(values, siteKey, SITE_FIELDS.validFrom), SITE_DTO_DATE_FORMAT) ||
            undefined,
          valid_to:
            formatDate(siteValue(values, siteKey, SITE_FIELDS.validTill), SITE_DTO_DATE_FORMAT) ||
            undefined,
        }
      : {}),
    address_line_1: orNull(siteValue(values, siteKey, SITE_FIELDS.addressLine1)),
    address_line_2: orNull(siteValue(values, siteKey, SITE_FIELDS.addressLine2)),
    city: orNull(siteValue(values, siteKey, SITE_FIELDS.city)),
    // TODO: wire real API — shipping_details has no country column
    country: orNull(siteValue(values, siteKey, SITE_FIELDS.country)),
    zip_code: orNull(siteValue(values, siteKey, SITE_FIELDS.zipCode)),
  };
};

export const buildCustomerSitesDto = (values) => ({
  shipping_details: siteKeysOf(values).map((siteKey) => buildCustomerSiteDto(values, siteKey)),
});

// PUT /shipping-details/:id/account/:account_id: confirmed against the controller, it never persists the site's own id, the certificate-verified flag, or a country.
export const buildCustomerSiteUpdateDto = (values) => {
  const site = buildCustomerSiteDto(values, FIRST_SITE_KEY);
  return {
    shippingFrom: site.shipping_from,
    shippingTo: site.shipping_from,
    siteManagerName: site.site_manager_name,
    siteManagerEmail: site.email,
    certificateNumber: site.certificate_number,
    validFrom: site.valid_from,
    validTo: site.valid_to,
    addressLine1: site.address_line_1,
    addressLine2: site.address_line_2,
    city: site.city,
    zipCode: site.zip_code,
  };
};

const detailFor = (dto, mode) =>
  (dto.transport_details ?? []).find((detail) => detail.transportation_type === mode) ?? null;

export const processTransportationRoute = (dto) => {
  const byMode = Object.fromEntries(
    TRANSPORT_MODES.map((mode) => {
      const detail = detailFor(dto, mode);
      const distance = detail?.distance_between_sites ?? null;
      const factor = detail?.emissions_per_km ?? null;
      return [
        mode,
        {
          distance: formatNumberOrDash(distance),
          emissionFactor: formatFactorOrDash(factor),
          total: distance == null || factor == null ? '-' : formatNumber(distance * factor),
        },
      ];
    }),
  );
  const formModeValues = Object.fromEntries(
    TRANSPORT_MODES.flatMap((mode) => {
      const detail = detailFor(dto, mode);
      return [
        [
          transportFieldName(mode, TRANSPORT_MODE_FIELDS.distance),
          formValue(detail?.distance_between_sites),
        ],
        [
          transportFieldName(mode, TRANSPORT_MODE_FIELDS.emissionFactor),
          formValue(detail?.emissions_per_km),
        ],
      ];
    }),
  );
  const shippingFromId = dto.customer_shipping_from?.id ?? dto.customer_shipping_from_id;
  const sentToId = dto.customer_sent_to?.id ?? dto.customer_sent_to_id;

  return {
    id: String(dto.id),
    routeName: formatTextOrDash(dto.route_name),
    shippingFrom: formatTextOrDash(dto.customer_shipping_from?.site_name),
    sentTo: formatTextOrDash(dto.customer_sent_to?.shipping_from),
    totalEmissions: formatNumberOrDash(dto.total_transportation_emissions),
    byMode,
    formValues: {
      ...EMPTY_TRANSPORT_FORM,
      [TRANSPORT_FIELDS.routeName]: formValue(dto.route_name),
      [TRANSPORT_FIELDS.shippingFrom]:
        shippingFromId == null ? '' : buildFacilityValue(FACILITY_KINDS.site, shippingFromId),
      [TRANSPORT_FIELDS.sentTo]: formValue(sentToId),
      ...formModeValues,
    },
  };
};

export const processTransportationRoutes = (dto) => {
  const rows = (dto?.output?.routes ?? []).map(processTransportationRoute);
  return { rows, totalCount: dto?.output?.total ?? rows.length };
};

// Only complete modes are sent; the server re-checks Σ(distance × factor) against the total.
export const buildTransportationRouteDto = (values) => ({
  routeName: trimmed(values[TRANSPORT_FIELDS.routeName]),
  customerShippingFromId: Number(parseFacilityValue(values[TRANSPORT_FIELDS.shippingFrom]).id),
  customerSentToId: Number(values[TRANSPORT_FIELDS.sentTo]),
  totalTransportationEmissions: totalTransportEmissions(values),
  transportDetails: TRANSPORT_MODES.filter((mode) => modeEmissions(values, mode) != null).map(
    (mode) => ({
      transportationType: mode,
      distanceBetweenSites: Number(
        values[transportFieldName(mode, TRANSPORT_MODE_FIELDS.distance)],
      ),
      emissionsPerKm: Number(
        values[transportFieldName(mode, TRANSPORT_MODE_FIELDS.emissionFactor)],
      ),
    }),
  ),
});
